<template>
  <div
    v-if="showLoadingState"
    key="loadingState"
  >
    <AtomLoadingSecondary/>
  </div>
  <div
    v-else
    key="artificialIntelligenceUsage"
    class="m-edition-billing-artificial-intelligence-usage"
  >
    <!--
      Los avisos del core-api. Si el registro esta apagado la tabla de generaciones esta vacia y
      TODO se ve en cero aunque la IA se este usando: sin este cartel el numero se lee como
      "nadie usa la IA", que es justo lo contrario.
    -->
    <div
      v-if="aviso"
      class="m-edition-billing-artificial-intelligence-usage-notice"
    >
      <span class="m-edition-billing-artificial-intelligence-usage-notice-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </span>
      <span>
        <b>{{ $t('mEditionBillingArtificialIntelligenceUsage.warnings.title') }}</b>
        <span class="display-block">{{ aviso }}</span>
      </span>
    </div>

    <!-- Rango de actividad: manda sobre los totales y sobre los tres graficos de abajo -->
    <form
      autocomplete="off"
      class="m-edition-billing-artificial-intelligence-usage-range"
      name="resumen-filters-form"
      @submit.prevent="readResumen"
    >
      <AtomInputPrimary
        v-model="resumenFilter.desde"
        :disabled="showResumenLoadingState"
        :label="$t('mEditionBillingArtificialIntelligenceUsage.filters.rangeFrom')"
        name="resumen_desde"
        type="date"
        wrapperClass="a-input-wrapper-column"
        @change="readResumen"
      />
      <AtomInputPrimary
        v-model="resumenFilter.hasta"
        :disabled="showResumenLoadingState"
        :label="$t('mEditionBillingArtificialIntelligenceUsage.filters.rangeTo')"
        name="resumen_hasta"
        type="date"
        wrapperClass="a-input-wrapper-column"
        @change="readResumen"
      />
    </form>

    <!-- Franja de totales del rango -->
    <h3 class="m-edition-billing-artificial-intelligence-usage-title">
      {{ $t('mEditionBillingArtificialIntelligenceUsage.totals.title') }}
      <small v-if="resumen.desde">
        {{ $t('mEditionBillingArtificialIntelligenceUsage.labels.range') }}
        {{ fechaCorta(resumen.desde) }}
        {{ $t('mEditionBillingArtificialIntelligenceUsage.labels.rangeTo') }}
        {{ fechaCorta(resumen.hasta) }}
      </small>
    </h3>

    <div
      v-if="showResumenLoadingState"
      key="resumenLoadingState"
    >
      <AtomLoadingSecondary/>
    </div>

    <template v-else>
    <ul class="m-edition-billing-artificial-intelligence-usage-tiles">
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-hero">
        <span class="tile-icon"><i class="fas fa-sack-dollar"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsage.totals.costUsd') }}</span>
        <span class="tile-value tile-value-hero">
          $ {{ formatUsd(resumenTotales.costo_usd) }}
          <span class="tile-unit">USD</span>
        </span>
        <!-- Sin registro el cero de arriba no significa "no se gasto", sino "no se anoto" -->
        <span
          v-if="!registroHabilitado"
          class="tile-detail text-color-warning"
        >
          {{ $t('mEditionBillingArtificialIntelligenceUsage.warnings.noRegistry') }}
        </span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-uso">
        <span class="tile-icon"><i class="fas fa-magic"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsage.totals.generations') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(resumenTotales.generaciones) }}</span>
        <span class="tile-detail">
          {{ $utils.formatCredits(resumenTotales.exitosas) }}
          {{ $t('mEditionBillingArtificialIntelligenceUsage.totals.successful') }}
          &bull;
          {{ $utils.formatCredits(resumenTotales.fallidas) }}
          {{ $t('mEditionBillingArtificialIntelligenceUsage.totals.failed') }}
        </span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-ok">
        <span class="tile-icon"><i class="fas fa-layer-group"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsage.totals.amount') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(resumenTotales.cantidad) }}</span>
      </li>
      <li class="m-edition-billing-artificial-intelligence-usage-tile tile-accent-credito">
        <span class="tile-icon"><i class="fas fa-coins"></i></span>
        <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsage.totals.credits') }}</span>
        <span class="tile-value">{{ $utils.formatCredits(resumenTotales.creditos) }}</span>
        <span class="tile-detail">
          {{ $t('mEditionBillingArtificialIntelligenceUsage.totals.creditsCharged') }}:
          {{ $utils.formatCredits(resumenTotales.creditos_cobrados) }}
        </span>
        <span
          v-if="!cobroHabilitado"
          class="tile-detail text-color-warning"
        >
          {{ $t('mEditionBillingArtificialIntelligenceUsage.warnings.noCharge') }}
        </span>
      </li>
    </ul>

    <!-- Los tres graficos generales del rango -->
    <div class="m-edition-billing-artificial-intelligence-usage-charts">
      <div class="m-edition-billing-artificial-intelligence-usage-card card-wide">
        <h4>{{ agrupaPorSemana
          ? $t('mEditionBillingArtificialIntelligenceUsage.charts.byWeekTitle')
          : $t('mEditionBillingArtificialIntelligenceUsage.charts.byDayTitle') }}</h4>
        <p class="card-subtitle">{{ agrupaPorSemana
          ? $t('mEditionBillingArtificialIntelligenceUsage.charts.byWeekSubtitle')
          : $t('mEditionBillingArtificialIntelligenceUsage.charts.byDaySubtitle') }}</p>
        <MoleculeChartColumns
          :color="colorSerie"
          :empty-text="$t('mEditionBillingArtificialIntelligenceUsage.charts.empty')"
          :formatter="formatUsdCorto"
          :series="serieGasto"
        />
      </div>

      <div class="m-edition-billing-artificial-intelligence-usage-card">
        <h4>{{ $t('mEditionBillingArtificialIntelligenceUsage.charts.byTypeTitle') }}</h4>
        <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsage.charts.byTypeSubtitle') }}</p>
        <MoleculeChartStack
          :empty-text="$t('mEditionBillingArtificialIntelligenceUsage.charts.empty')"
          :series="serieTipos"
        />
      </div>

      <div class="m-edition-billing-artificial-intelligence-usage-card">
        <h4>{{ $t('mEditionBillingArtificialIntelligenceUsage.charts.byModelTitle') }}</h4>
        <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsage.charts.byModelSubtitle') }}</p>
        <MoleculeChartBars
          :color="colorSerie"
          :empty-text="$t('mEditionBillingArtificialIntelligenceUsage.charts.empty')"
          :series="serieModelos"
        />
      </div>
    </div>
    </template>

    <!-- Filtros del listado. Son otros: estos buscan por la COMPRA, no por la actividad -->
    <div class="align-items-baseline display-flex-row">
      <form
        autocomplete="off"
        class="m-form-search m-edition-billing-artificial-intelligence-usage-filters"
        name="ventas-filters-form"
        @submit.prevent="searchSimpleFilter"
      >
        <AtomInputPrimary
          v-model="simpleFilter.email"
          :disabled="pagination.showLoadingState"
          :placeholder="$t('mEditionBillingArtificialIntelligenceUsage.filters.email')"
          name="email"
          type="text"
          wrapperClass="a-input-wrapper-row"
          v-on:keyup.enter="searchSimpleFilter"
        />

        <AtomSelectPrimary
          v-model="simpleFilter.modalidad"
          :disabled="pagination.showLoadingState"
          :elements="modalidadElements"
          keyField="id"
          name="modalidad"
          textField="name"
          wrapperClass="a-input-wrapper-row"
          @change="searchSimpleFilter"
        />

        <AtomSelectPrimary
          v-model="simpleFilter.vigente"
          :disabled="pagination.showLoadingState"
          :elements="vigenteElements"
          keyField="id"
          name="vigente"
          textField="name"
          wrapperClass="a-input-wrapper-row"
          @change="searchSimpleFilter"
        />

        <AtomSelectPrimary
          v-model="simpleFilter.incluir_eliminadas"
          :disabled="pagination.showLoadingState"
          :elements="eliminadasElements"
          keyField="id"
          name="incluir_eliminadas"
          textField="name"
          wrapperClass="a-input-wrapper-row"
          @change="searchSimpleFilter"
        />

        <AtomButtonSecondary
          :button-secondary="buttonSecondary.reload"
          :disabled="pagination.showLoadingState"
          @reloadListing="reloadListing"
        />
      </form>
    </div>

    <!--
      Lo que paga el cliente y lo que cuesta el proveedor, uno al lado del otro y NUNCA sumados:
      el cliente paga en su moneda y el proveedor cobra en dolares, y no hay tipo de cambio guardado
      en ningun lado. Por eso el cobrado va agrupado por divisa y el costo aparte, con su unidad.
    -->
    <div class="m-edition-billing-artificial-intelligence-usage-card margin-bottom-large">
      <h4>{{ $t('mEditionBillingArtificialIntelligenceUsage.income.title') }}</h4>
      <p class="card-subtitle">{{ $t('mEditionBillingArtificialIntelligenceUsage.income.subtitle') }}</p>
      <div class="m-edition-billing-artificial-intelligence-usage-income">
        <div class="income-side">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsage.income.paid') }}</span>
          <p
            v-if="!ingresosPorDivisa.length"
            class="income-empty"
          >
            {{ $t('mEditionBillingArtificialIntelligenceUsage.income.empty') }}
          </p>
          <ul v-else>
            <li
              v-for="divisa in ingresosPorDivisa"
              :key="'divisa-' + divisa.key"
            >
              <span class="income-amount">
                {{ divisa.simbolo }} {{ divisa.monto.toFixed(2) }}
                <span class="text-color-black-35">{{ divisa.iso }}</span>
              </span>
              <small class="income-note">
                {{ divisa.pagos }} {{ $t('mEditionBillingArtificialIntelligenceUsage.income.payments') }}
                &bull;
                {{ divisa.licencias }} {{ $t('mEditionBillingArtificialIntelligenceUsage.income.licenses') }}
              </small>
            </li>
          </ul>
        </div>
        <div class="income-side">
          <span class="tile-label">{{ $t('mEditionBillingArtificialIntelligenceUsage.income.cost') }}</span>
          <span class="income-amount">
            $ {{ formatUsd(costoPaginaUsd) }}
            <span class="text-color-black-35">USD</span>
          </span>
          <small class="income-note">
            {{ $utils.formatCredits(generacionesPagina) }}
            {{ $t('mEditionBillingArtificialIntelligenceUsage.labels.generations') }}
          </small>
        </div>
      </div>
      <p
        v-if="avisoDivisas"
        class="m-edition-billing-artificial-intelligence-usage-currency-note"
      >
        <i class="fas fa-info-circle margin-right-xsmall"></i>
        <span>{{ avisoDivisas }}</span>
      </p>
    </div>

    <div class="m-table-primary">
      <table>
        <tr v-show="ventas.length">
          <th class="text-align-left">
            <a
              href="#"
              :class="claseOrden('email')"
              :data-tooltip-text="tooltipOrden('email')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('email')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thClient') }}
              <i :class="iconoOrden('email')"></i>
            </a>
          </th>
          <th class="text-align-left">
            {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thPlan') }}
          </th>
          <th class="text-align-center">
            {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thState') }}
          </th>
          <th class="text-align-right">
            <a
              href="#"
              :class="claseOrden('creditos')"
              :data-tooltip-text="tooltipOrden('creditos')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('creditos')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thCredits') }}
              <i :class="iconoOrden('creditos')"></i>
            </a>
          </th>
          <th class="text-align-right">
            <a
              href="#"
              :class="claseOrden('consumo_ciclo')"
              :data-tooltip-text="tooltipOrden('consumo_ciclo')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('consumo_ciclo')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thCycle') }}
              <i :class="iconoOrden('consumo_ciclo')"></i>
            </a>
          </th>
          <th class="text-align-right">
            <a
              href="#"
              :class="claseOrden('consumo')"
              :data-tooltip-text="tooltipOrden('consumo')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('consumo')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thAccumulated') }}
              <i :class="iconoOrden('consumo')"></i>
            </a>
          </th>
          <th class="text-align-right">
            <a
              href="#"
              :class="claseOrden('generaciones')"
              :data-tooltip-text="tooltipOrden('generaciones')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('generaciones')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thGenerations') }}
              <i :class="iconoOrden('generaciones')"></i>
            </a>
          </th>
          <th class="text-align-right">
            <a
              href="#"
              :class="claseOrden('costo')"
              :data-tooltip-text="tooltipOrden('costo')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('costo')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thCostUsd') }}
              <i :class="iconoOrden('costo')"></i>
            </a>
          </th>
          <th class="text-align-right">
            {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thIncome') }}
          </th>
          <th class="text-align-center">
            <a
              href="#"
              :class="claseOrden('fecha')"
              :data-tooltip-text="tooltipOrden('fecha')"
              class="a-tooltip-primary a-tooltip-bottom"
              @click.prevent="ordenarPor('fecha')"
            >
              {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thDate') }}
              <i :class="iconoOrden('fecha')"></i>
            </a>
          </th>
          <th class="text-align-center">
            {{ $t('mEditionBillingArtificialIntelligenceUsage.table.thRecord') }}
          </th>
        </tr>
        <tr
          v-for="venta in ventas"
          :key="venta.licencia"
          class="row-data"
        >
          <td class="text-align-left">
            <span class="display-flex-column user-info">
              <span class="ellipsis">
                {{ venta.nombre || $t('mEditionBillingArtificialIntelligenceUsage.labels.noName') }}
              </span>
              <small class="ellipsis font-weight-regular">{{ venta.email }}</small>
            </span>
          </td>
          <td class="text-align-left">
            <span class="display-flex-column">
              <span class="ellipsis">{{ venta.plan }}</span>
              <small class="font-weight-regular">
                {{ $t(`mEditionBillingArtificialIntelligenceUsage.modalities.${venta.modalidad}`) }}
              </small>
            </span>
          </td>
          <td class="text-align-center">
            <span :class="['ia-chip', claseEstado(venta)]">
              {{ $t(`mEditionBillingArtificialIntelligenceUsage.states.${estado(venta)}`) }}
            </span>
          </td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(venta.creditos) }}
          </td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(venta.consumo_ciclo) }}
          </td>
          <td class="text-align-right text-white-space-nowrap">
            {{ $utils.formatCredits(venta.consumo_acumulado) }}
          </td>
          <td class="text-align-right text-white-space-nowrap">
            <span class="display-flex-column">
              <span>{{ $utils.formatCredits(venta.generaciones) }}</span>
              <small
                v-if="venta.generaciones_fallidas"
                class="font-weight-regular text-color-danger"
              >
                {{ $utils.formatCredits(venta.generaciones_fallidas) }}
                {{ $t('mEditionBillingArtificialIntelligenceUsage.tooltips.failed') }}
              </small>
            </span>
          </td>
          <td class="text-align-right text-white-space-nowrap">
            $ {{ formatUsd(venta.costo_usd) }}
            <span class="text-color-black-35">USD</span>
          </td>
          <td class="text-align-right text-white-space-nowrap">
            <span v-if="venta.ingreso && venta.ingreso.pagos">
              {{ venta.ingreso.divisa_simbolo || '' }} {{ Number(venta.ingreso.monto).toFixed(2) }}
              <span class="text-color-black-35">{{ venta.ingreso.divisa || '' }}</span>
            </span>
            <span
              v-else
              class="text-color-black-35"
            >
              &mdash;
            </span>
          </td>
          <td class="text-align-center text-white-space-nowrap">
            {{ fechaCompra(venta.fecha_compra) }}
          </td>
          <td class="text-align-center">
            <a
              :data-tooltip-text="$t('mEditionBillingArtificialIntelligenceUsage.tooltips.record')"
              :href="urlFicha(venta)"
              class="a-button-secondary a-button-billing-secondary a-button-icon-rounded a-tooltip-primary a-tooltip-bottom"
              @click.prevent="irAFicha(venta)"
            >
              <i class="fas fa-search-plus"></i>
            </a>
          </td>
        </tr>
      </table>
      <div
        v-show="!ventas.length"
        class="text-align-center"
      >
        <p class="margin-bottom-zero">
          {{ $t('mEditionBillingArtificialIntelligenceUsage.messages.noData') }}
        </p>
        <small>{{ $t('mEditionBillingArtificialIntelligenceUsage.messages.noDataHint') }}</small>
      </div>
      <MoleculePaginationPrimary
        :pagination="pagination"
        @setPaginationCurrentPage="setPaginationCurrentPage"
      />
    </div>
  </div>
</template>

<script>
import configApp from '@/config'
import mEditionMenu from '@/plugins/languages/es/molecules/m-edition-section-navigation-menu'

/*
 | Monitoreo interno del consumo de Inteligencia Artificial (solo el dueño de Vidroop, desde
 | app-sales). Responde dos preguntas: en que se usa la IA y si lo que cuesta el proveedor entra
 | dentro de lo que paga el cliente.
 |
 | Dos llamadas independientes, cada una con SUS filtros, y por eso hay dos filas de filtros:
 |  - /monitoreo/resumen: los totales y los tres graficos, filtrados por fecha de ACTIVIDAD.
 |  - /monitoreo/ventas:  la tabla, filtrada por la COMPRA (email, modalidad, vigencia).
 | Mezclar los dos rangos en un solo filtro diria una cosa distinta de la que muestra cada bloque.
 |
 | Los graficos estan hechos a mano con HTML y CSS (MoleculeChart*): app-sales no tiene ninguna
 | libreria de graficos y un panel interno no justifica agregar una dependencia.
 */

/*
 | Paleta categorica del reparto por tipo. Validada para daltonismo sobre fondo blanco (peor par
 | deltaE 9.2 deutan, 24.0 con vision normal). El verde agua no llega a 3:1 contra el blanco, asi que
 | el reparto SIEMPRE escribe los valores al lado de la leyenda y nunca depende solo del color.
 */
const PALETA_TIPOS = ['#2a78d6', '#eb6834', '#1baf7a']

// Color unico de los graficos de una sola serie (gasto por dia, modelos)
const COLOR_SERIE = '#2a78d6'

// Mas dias que esto en el grafico de gasto y las columnas quedan hilos: se agrupa por semana
const MAXIMO_DIAS_GRAFICO = 92

// Cuantos modelos se dibujan antes de plegar el resto en "Otros modelos"
const MAXIMO_MODELOS = 8

// Los campos de `orden` que acepta el core-api
const ORDENES = ['fecha', 'costo', 'consumo', 'consumo_ciclo', 'generaciones', 'creditos', 'email']

const TOTALES_VACIOS = {
  cantidad: 0,
  costo_usd: 0,
  creditos: 0,
  creditos_cobrados: 0,
  exitosas: 0,
  fallidas: 0,
  generaciones: 0
}

export default {
  name: 'MoleculeEditionBillingArtificialIntelligenceUsage',

  components: {
    MoleculeChartBars: () => import('@/components/molecules/MoleculeChartBars'),
    MoleculeChartColumns: () => import('@/components/molecules/MoleculeChartColumns'),
    MoleculeChartStack: () => import('@/components/molecules/MoleculeChartStack'),
    MoleculePaginationPrimary: () => import('@/components/molecules/MoleculePaginationPrimary')
  },

  data () {
    return {
      aviso: '',

      avisoDivisas: '',

      // Los dos interruptores del core-api. Arrancan encendidos para no gritar antes de saber
      cobroHabilitado: true,

      buttonSecondary: {
        reload: {
          class: 'a-button-billing-secondary a-button-side a-tooltip-primary a-tooltip-bottom',
          component: '',
          icon: 'fas fa-sync-alt',
          method: 'reloadListing',
          text: '',
          tooltip: this.$t('mEditionBillingArtificialIntelligenceUsage.tooltips.reload'),
          type: 'button'
        }
      },

      colorSerie: COLOR_SERIE,

      /*
       | El valor por defecto va vacio y no en 0: asi el parametro ni se manda y el core-api aplica
       | su propio default, que es justamente ocultar las eliminadas. Ademas es como se arman los
       | demas selects de app-sales, donde la primera opcion siempre vale ''
       */
      eliminadasElements: [
        { id: '', name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.deletedNo') },
        { id: 1, name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.deletedAll') }
      ],

      filter: {
        direccion: 'desc',
        email: '',
        incluir_eliminadas: '',
        limite: 25,
        modalidad: '',
        orden: 'fecha',
        pagina: 1,
        vigente: ''
      },

      modalidadElements: [
        { id: '', name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.modalityAll') },
        { id: 'mensual', name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.modalityMonthly') },
        { id: 'creditos', name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.modalityCredits') }
      ],

      pagination: {
        currentPage: 1,
        currentQuantity: 0,
        limit: 25,
        showLoadingState: false,
        totalPages: 0,
        totalQuantity: 0
      },

      registroHabilitado: true,

      resumen: {},

      resumenFilter: {
        desde: '',
        hasta: ''
      },

      showLoadingState: true,

      showResumenLoadingState: true,

      simpleFilter: {
        email: '',
        incluir_eliminadas: '',
        modalidad: '',
        vigente: ''
      },

      ventas: [],

      vigenteElements: [
        { id: '', name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.validAll') },
        { id: 1, name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.validYes') },
        { id: 0, name: this.$t('mEditionBillingArtificialIntelligenceUsage.filters.validNo') }
      ]
    }
  },

  computed: {
    // El grafico pasa de dias a semanas cuando el rango es largo, para que las columnas se vean
    agrupaPorSemana () {
      return this.puntosPorDia.length > MAXIMO_DIAS_GRAFICO
    },

    // Lo que costo el proveedor por las licencias de esta pagina del listado
    costoPaginaUsd () {
      return this.ventas.reduce((suma, venta) => suma + (Number(venta.costo_usd) || 0), 0)
    },

    generacionesPagina () {
      return this.ventas.reduce((suma, venta) => suma + (Number(venta.generaciones) || 0), 0)
    },

    /*
     | Lo cobrado a los clientes de esta pagina, AGRUPADO POR DIVISA y nunca sumado entre monedas:
     | no hay tipo de cambio guardado en core-api y convertir a ojo seria inventar un numero.
     */
    ingresosPorDivisa () {
      const porDivisa = {}

      this.ventas.forEach(venta => {
        const ingreso = venta.ingreso

        if (!ingreso || !ingreso.pagos) {
          return
        }

        const iso = ingreso.divisa || ''

        if (!porDivisa[iso]) {
          porDivisa[iso] = {
            iso: iso,
            key: iso || 'sin-divisa',
            licencias: 0,
            monto: 0,
            pagos: 0,
            simbolo: ingreso.divisa_simbolo || ''
          }
        }

        porDivisa[iso].licencias += 1
        porDivisa[iso].monto += Number(ingreso.monto) || 0
        porDivisa[iso].pagos += Number(ingreso.pagos) || 0
      })

      return Object.keys(porDivisa)
        .map(iso => porDivisa[iso])
        .sort((a, b) => b.monto - a.monto)
    },

    /*
     | La serie del grafico de gasto. El core-api manda SOLO los dias con actividad, asi que el
     | relleno de ceros lo hace el front: un dia sin generaciones costo cero, no "no se sabe".
     */
    puntosPorDia () {
      const porFecha = {}
      const puntos = []
      const dias = this.resumen.por_dia || []

      dias.forEach(dia => {
        porFecha[dia.fecha] = Number(dia.costo_usd) || 0
      })

      if (!this.resumen.desde || !this.resumen.hasta) {
        return []
      }

      const cursor = this.$moment(this.resumen.desde, 'YYYY-MM-DD')
      const fin = this.$moment(this.resumen.hasta, 'YYYY-MM-DD')

      while (cursor.isSameOrBefore(fin, 'day')) {
        const fecha = cursor.format('YYYY-MM-DD')

        puntos.push({ fecha: fecha, valor: porFecha[fecha] || 0 })
        cursor.add(1, 'days')
      }

      return puntos
    },

    resumenTotales () {
      return Object.assign({}, TOTALES_VACIOS, this.resumen.totales || {})
    },

    serieGasto () {
      if (!this.agrupaPorSemana) {
        return this.puntosPorDia.map(punto => ({
          key: punto.fecha,
          label: this.fechaCorta(punto.fecha),
          tooltip: `${this.fechaCorta(punto.fecha)} · $ ${this.formatUsd(punto.valor)} USD`,
          value: punto.valor
        }))
      }

      const semanas = []

      this.puntosPorDia.forEach((punto, index) => {
        const bloque = Math.floor(index / 7)

        if (!semanas[bloque]) {
          semanas[bloque] = { fecha: punto.fecha, valor: 0 }
        }

        semanas[bloque].valor += punto.valor
      })

      return semanas.map(semana => ({
        key: semana.fecha,
        label: this.fechaCorta(semana.fecha),
        tooltip: `${this.$t('mEditionBillingArtificialIntelligenceUsage.charts.weekOf')} ${this.fechaCorta(semana.fecha)} · $ ${this.formatUsd(semana.valor)} USD`,
        value: semana.valor
      }))
    },

    // Los modelos que mas costaron; el resto se pliega en una sola fila "Otros modelos"
    serieModelos () {
      const modelos = (this.resumen.por_modelo || []).map(modelo => ({
        key: `${modelo.proveedor || 'sin-proveedor'}-${modelo.modelo || 'sin-modelo'}`,
        label: this.nombreModelo(modelo),
        text: `$ ${this.formatUsd(modelo.costo_usd)}`,
        value: Number(modelo.costo_usd) || 0
      }))

      if (modelos.length <= MAXIMO_MODELOS) {
        return modelos
      }

      const visibles = modelos.slice(0, MAXIMO_MODELOS)
      const resto = modelos.slice(MAXIMO_MODELOS)
        .reduce((suma, modelo) => suma + modelo.value, 0)

      return visibles.concat([{
        key: 'otros',
        label: this.$t('mEditionBillingArtificialIntelligenceUsage.charts.others'),
        text: `$ ${this.formatUsd(resto)}`,
        value: resto
      }])
    },

    // El reparto del costo entre los tres tipos de generacion, en el orden que manda el core-api
    serieTipos () {
      return (this.resumen.por_tipo || []).map((tipo, index) => ({
        color: PALETA_TIPOS[index % PALETA_TIPOS.length],
        key: tipo.tipo,
        label: this.$t(`mEditionBillingArtificialIntelligenceUsage.types.${tipo.tipo}`),
        text: `$ ${this.formatUsd(tipo.costo_usd)}`,
        value: Number(tipo.costo_usd) || 0
      }))
    }
  },

  created () {
    // Con el flag apagado el item no esta en el menu; la URL directa tampoco muestra la pantalla
    if (configApp.droopInteligenciaArtificialHabilitado !== true) {
      this.$router.replace(`/${this.$t('rPaths.billing')}/${this.$t('mEditionMenu.billing.satellites.subject')}`)

      return
    }

    this.readResumen()
    this.readVentas()
  },

  methods: {
    /*
     | El estado va como CHIP y no como texto de color: en una columna angosta un texto suelto se
     | partia letra por letra, y ademas el color solo no dice nada a quien no lo conoce. La capsula
     | no se puede partir y se lee como etiqueta.
     */
    claseEstado (venta) {
      const clases = {
        activa: 'ia-chip-ok',
        agotada: 'ia-chip-alerta',
        eliminada: 'ia-chip-off',
        suspendida: 'ia-chip-alerta'
      }

      return clases[this.estado(venta)]
    },

    claseOrden (campo) {
      return this.filter.orden === campo ? 'order-link order-link-active' : 'order-link'
    },

    // "Agotada" no es un estado guardado: la licencia sigue vigente pero sin disponible
    estado (venta) {
      if (venta.eliminada) {
        return 'eliminada'
      }

      if (!venta.vigente) {
        return 'suspendida'
      }

      return venta.agotada ? 'agotada' : 'activa'
    },

    fechaCorta (fecha) {
      return fecha ? this.$moment(fecha, 'YYYY-MM-DD').format('D MMM') : ''
    },

    fechaCompra (fecha) {
      return fecha ? this.$moment(fecha, 'YYYY-MM-DD HH:mm:ss').format('D MMM YYYY') : ''
    },

    /*
     | Los importes se escriben con dos decimales, como en el resto de app-sales. Por debajo de un
     | centavo se escriben con seis: una generacion suelta cuesta fracciones de centavo y con dos
     | decimales se veria "0.00", que no es lo mismo que gratis.
     */
    formatUsd (valor) {
      const numero = Number(valor) || 0

      return numero !== 0 && Math.abs(numero) < 0.01 ? numero.toFixed(6) : numero.toFixed(2)
    },

    // El eje del grafico: sin simbolo ni unidad, que van en el titulo del bloque
    formatUsdCorto (valor) {
      const numero = Number(valor) || 0

      return numero !== 0 && Math.abs(numero) < 0.01 ? numero.toFixed(4) : numero.toFixed(2)
    },

    iconoOrden (campo) {
      if (this.filter.orden !== campo) {
        return 'fas fa-sort margin-left-small text-color-black-35'
      }

      return this.filter.direccion === 'asc'
        ? 'fas fa-sort-amount-up-alt margin-left-small'
        : 'fas fa-sort-amount-down margin-left-small'
    },

    irAFicha (venta) {
      this.$router.push(this.urlFicha(venta))
    },

    nombreModelo (modelo) {
      const proveedor = modelo.proveedor || ''
      const nombre = modelo.modelo || ''

      return [proveedor, nombre].filter(parte => parte.length).join(' · ') || '—'
    },

    ordenarPor (campo) {
      if (ORDENES.indexOf(campo) === -1) {
        return
      }

      if (this.filter.orden === campo) {
        this.filter.direccion = this.filter.direccion === 'desc' ? 'asc' : 'desc'
      } else {
        this.filter.orden = campo
        // El email se lee alfabeticamente; todo lo demas interesa de mayor a menor
        this.filter.direccion = campo === 'email' ? 'asc' : 'desc'
      }

      this.setPaginationCurrentPage(1)
    },

    readResumen () {
      this.showResumenLoadingState = true

      const filter = {
        desde: this.resumenFilter.desde || '',
        hasta: this.resumenFilter.hasta || ''
      }

      this.$services.read('sateliteIaMonitoreo', 'readMonitoreoIaResumen', {}, [], filter)
        .then(response => {
          this.resumen = response.data
          this.aviso = response.data.aviso || ''
          this.cobroHabilitado = response.data.cobro_habilitado !== false
          this.registroHabilitado = response.data.registro_habilitado !== false
          this.showResumenLoadingState = false
        })
        .catch(error => {
          console.log(error)
          this.resumen = {}
          this.showResumenLoadingState = false
          this.$alerts.pushAlert('mEditionBillingArtificialIntelligenceUsage', 'readResumen', 'error', 'a-alert-danger')
        })
    },

    readVentas () {
      if (this.pagination.showLoadingState) {
        return
      }

      this.pagination.showLoadingState = true

      this.$services.read('sateliteIaMonitoreo', 'readMonitoreoIaVentas', {}, [], this.filter)
        .then(response => {
          this.ventas = response.data.data || []
          this.aviso = response.data.aviso || this.aviso
          this.avisoDivisas = response.data.aviso_divisas || ''
          this.pagination.currentQuantity = this.ventas.length
          this.pagination.limit = response.data.limite || this.filter.limite
          this.pagination.totalPages = response.data.paginas || 0
          this.pagination.totalQuantity = response.data.total || 0
          this.pagination.showLoadingState = false
          this.showLoadingState = false
        })
        .catch(error => {
          console.log(error)
          this.ventas = []
          this.pagination.currentQuantity = 0
          this.pagination.totalPages = 0
          this.pagination.totalQuantity = 0
          this.pagination.showLoadingState = false
          this.showLoadingState = false
          this.$alerts.pushAlert('mEditionBillingArtificialIntelligenceUsage', 'readVentas', 'error', 'a-alert-danger')
        })
    },

    reloadListing () {
      this.simpleFilter = {
        email: '',
        incluir_eliminadas: '',
        modalidad: '',
        vigente: ''
      }

      this.filter = Object.assign({}, this.filter, {
        direccion: 'desc',
        email: '',
        incluir_eliminadas: '',
        modalidad: '',
        orden: 'fecha',
        vigente: ''
      })

      this.setPaginationCurrentPage(1)
    },

    searchSimpleFilter () {
      this.filter.email = this.simpleFilter.email
      this.filter.incluir_eliminadas = this.simpleFilter.incluir_eliminadas
      this.filter.modalidad = this.simpleFilter.modalidad
      this.filter.vigente = this.simpleFilter.vigente
      this.setPaginationCurrentPage(1)
    },

    setPaginationCurrentPage (page) {
      this.filter.pagina = page
      this.pagination.currentPage = page
      this.readVentas()
    },

    tooltipOrden (campo) {
      const invertir = this.filter.orden === campo && this.filter.direccion === 'desc'

      return invertir
        ? this.$t('mEditionBillingArtificialIntelligenceUsage.tooltips.sortAsc')
        : this.$t('mEditionBillingArtificialIntelligenceUsage.tooltips.sortDesc')
    },

    urlFicha (venta) {
      const menu = mEditionMenu.mEditionMenu.billing.artificialIntelligenceUsage

      return `/${this.$t('rPaths.billing')}/${menu.subject}/${venta.licencia}/${menu.children.record.subedition}`
    }
  }
}
</script>
